import React, { useState, useEffect } from "react"
import { parentDashboardApi } from "../../lib/api"
import ParentAssignmentSection from "./ParentAssignmentSection"
import AttentionIndicator from "./AttentionIndicator"

// Avatar colors matching StudentOverviewCard
const avatarColors = [
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-blue-600",
  "from-fuchsia-500 to-pink-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-teal-600",
  "from-rose-500 to-red-600",
]

const StudentDetailPanel = ({ studentId, termId }) => {
  const [student, setStudent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (studentId) {
      fetchStudentDetail()
    }
  }, [studentId])

  // Filter assignments by term
  const filterByTerm = (assignments) => {
    if (!termId) return assignments || []
    return (assignments || []).filter(a => a.course?.term_id === termId)
  }

  const fetchStudentDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await parentDashboardApi.getStudentDetail(studentId)
      setStudent(data.student)
    } catch (err) {
      setError(err.message || "Failed to load student details")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[var(--cream)] rounded-xl"></div>
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-[var(--cream)] rounded-lg w-1/3"></div>
                <div className="h-4 bg-[var(--cream)] rounded-lg w-1/2"></div>
              </div>
            </div>
            <div className="h-24 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-24 bg-[var(--cream)] rounded-xl"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-50 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button
            onClick={fetchStudentDetail}
            className="px-4 py-2 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!student) {
    return null
  }

  const { assignments, courses } = student
  const avatarColor = avatarColors[(studentId || 0) % avatarColors.length]

  // Filter courses by term
  const filteredCourses = termId
    ? (courses || []).filter(c => c.term_id === termId)
    : (courses || [])

  // Get grade color based on score
  const getGradeColor = (score) => {
    if (score == null) return "text-[var(--ink-light)] bg-[var(--cream)]"
    if (score >= 90) return "text-emerald-700 bg-emerald-100"
    if (score >= 80) return "text-blue-700 bg-blue-100"
    if (score >= 70) return "text-amber-700 bg-amber-100"
    if (score >= 60) return "text-orange-700 bg-orange-100"
    return "text-red-700 bg-red-100"
  }

  // Apply term filter to assignments
  const filteredAssignments = {
    past_due: filterByTerm(assignments.past_due),
    due_soon: filterByTerm(assignments.due_soon),
    due_today: filterByTerm(assignments.due_today),
    due_this_week: filterByTerm(assignments.due_this_week),
    due_next_week: filterByTerm(assignments.due_next_week),
    submitted_today: filterByTerm(assignments.submitted_today),
    recently_submitted: filterByTerm(assignments.recently_submitted),
    graded_today: filterByTerm(assignments.graded_today),
    recently_graded: filterByTerm(assignments.recently_graded),
    completed: filterByTerm(assignments.completed),
  }

  // Calculate stats from filtered assignments
  const filteredStats = {
    overdue: filteredAssignments.past_due.length,
    due_today: filteredAssignments.due_today.length,
    due_this_week: filteredAssignments.due_this_week.length,
  }
  const attentionScore = (filteredStats.overdue * 3) + (filteredStats.due_today * 2)

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="relative overflow-hidden">
        {/* Gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${avatarColor} opacity-95`} />

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id={`grid-${studentId}`} width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#grid-${studentId})`} />
          </svg>
        </div>

        <div className="relative p-6 text-white">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center font-display text-2xl font-bold shadow-lg">
              {student.name?.charAt(0)?.toUpperCase() || student.email?.charAt(0)?.toUpperCase()}
            </div>

            {/* Info */}
            <div>
              <h2 className="font-display text-2xl font-bold">{student.name}</h2>
              <p className="text-white/80 text-sm">{student.email}</p>
            </div>
          </div>

          {/* Stats summary */}
          <div className="mt-5 flex flex-wrap items-center gap-4 sm:gap-6">
            <AttentionIndicator score={attentionScore} size="lg" light />

            <div className="flex flex-wrap gap-3 sm:gap-4 text-sm">
              <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${filteredStats.overdue > 0 ? "bg-white/20" : "bg-white/10"}`}>
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <strong>{filteredStats.overdue}</strong> overdue
              </span>
              <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${filteredStats.due_today > 0 ? "bg-white/20" : "bg-white/10"}`}>
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <strong>{filteredStats.due_today}</strong> today
              </span>
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10">
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <strong>{filteredStats.due_this_week}</strong> this week
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Course Grades Section */}
      {filteredCourses.length > 0 && filteredCourses.some(c => c.current_score != null || c.current_grade) && (
        <div className="px-4 sm:px-6 pt-4 sm:pt-6">
          <div className="bg-[var(--paper-warm)] rounded-xl p-4">
            <h3 className="font-semibold text-[var(--ink)] mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-[var(--ink-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Course Grades
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredCourses
                .filter(c => c.current_score != null || c.current_grade)
                .map(course => (
                  <div
                    key={course.id}
                    className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2"
                  >
                    <span className="text-sm text-[var(--ink)] truncate font-medium">
                      {course.course_code || course.name}
                    </span>
                    <span className={`text-sm font-semibold px-2 py-0.5 rounded ${getGradeColor(course.current_score)}`}>
                      {course.current_grade
                        ? `${course.current_grade}${course.current_score ? ` (${Math.round(course.current_score)}%)` : ""}`
                        : `${Math.round(course.current_score)}%`
                      }
                    </span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Assignment sections */}
      <div className="p-4 sm:p-6 space-y-2">
        <ParentAssignmentSection
          title="Past Due"
          assignments={filteredAssignments.past_due}
          urgency="overdue"
          emptyMessage="No overdue assignments"
          collapsed
        />

        <ParentAssignmentSection
          title="Due Soon"
          assignments={filteredAssignments.due_soon}
          urgency="due_soon"
          emptyMessage="Nothing due in the next 48 hours"
        />

        <ParentAssignmentSection
          title="Due Today"
          assignments={filteredAssignments.due_today}
          urgency="today"
          emptyMessage="Nothing due today"
          collapsed
        />

        <ParentAssignmentSection
          title="Due This Week"
          assignments={filteredAssignments.due_this_week}
          urgency="this_week"
          emptyMessage="Nothing due this week"
          collapsed
        />

        <ParentAssignmentSection
          title="Due Next Week"
          assignments={filteredAssignments.due_next_week}
          urgency="next_week"
          emptyMessage="Nothing due next week"
          collapsed
        />

        <ParentAssignmentSection
          title="Submitted Today"
          assignments={filteredAssignments.submitted_today}
          urgency="submitted_today"
          collapsed
        />

        <ParentAssignmentSection
          title="Recently Submitted"
          assignments={filteredAssignments.recently_submitted}
          urgency="recently_submitted"
          collapsed
        />

        <ParentAssignmentSection
          title="Graded Today"
          assignments={filteredAssignments.graded_today}
          urgency="graded_today"
          collapsed
        />

        <ParentAssignmentSection
          title="Recently Graded"
          assignments={filteredAssignments.recently_graded}
          urgency="graded"
          collapsed
        />

        <ParentAssignmentSection
          title="Completed"
          assignments={filteredAssignments.completed}
          urgency="completed"
          collapsed
        />
      </div>
    </div>
  )
}

export default StudentDetailPanel
